import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, Hammer, Home as HomeIcon, Ruler, Handshake, PaintbrushVertical, Sparkles, ShieldCheck, PackageOpen, RotateCw, Image as ImageIcon } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import MobileStickyBar from "../components/MobileStickyBar";
import Seo from "../components/Seo";
import QuoteForm from "../components/QuoteForm";
import BeforeAfter from "../components/BeforeAfter";
import { COMPANY, api, imgUrl, projectCover } from "../lib/site";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "../i18n/LanguageContext";

export default function Home() {
  const { t } = useLang();
  const { data: projects = [] } = useQuery({ queryKey: ["projects","home"], queryFn: async () => (await api.get("/projects")).data });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const { data: heroSetting } = useQuery({ queryKey: ["settings","hero"], queryFn: async () => (await api.get("/settings/hero")).data });
  const { data: pageImagesSetting } = useQuery({ queryKey: ["settings","page_images"], queryFn: async () => { try { return (await api.get("/settings/page_images")).data; } catch { return null; } } });
  const hero = heroSetting?.value || {};
  const pageImages = pageImagesSetting?.value || {};
  const homeAboutImg = pageImages.home_about_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${pageImages.home_about_id}` : (pageImages.home_about_url || "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=1400&q=85&auto=format&fit=crop");
  const projectFallback = pageImages.project_fallback_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${pageImages.project_fallback_id}` : (pageImages.project_fallback_url || "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=900");
  const heroAfter = hero.after_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${hero.after_id}` : hero.after_url;
  const heroBefore = hero.before_id ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${hero.before_id}` : hero.before_url;
  const [showBefore, setShowBefore] = useState(false);
  const [openCompare, setOpenCompare] = useState(false);
  const featured = projects.slice(0, 5);

  const trust = [
    { icon: <HomeIcon size={18} />, title: t("trust.local.title"), desc: t("trust.local.desc") },
    { icon: <PackageOpen size={18} />, title: t("trust.total.title"), desc: t("trust.total.desc") },
    { icon: <Sparkles size={18} />, title: t("trust.materials.title"), desc: t("trust.materials.desc") },
    { icon: <Handshake size={18} />, title: t("trust.coop.title"), desc: t("trust.coop.desc") },
  ];

  const aboutPoints = [
    "Totalpris på jobben",
    "Kvalitetsmaterialer til gode betingelser",
    "Samarbeid med andre firmaer ved større prosjekter",
    "Kan ordne med tegninger for tilbygg og lignende",
    "Effektiv gjennomføring og god kommunikasjon",
  ];

  const heroImg = showBefore && heroBefore ? heroBefore : heroAfter;

  return (
    <div className="min-h-screen">
      <Seo title="Tømrer i Halden – snekker & byggtjenester | NOUROZI EBYGG & ANLEGG" description="Tømrer og snekker i Halden. NOUROZI EBYGG & ANLEGG utfører tømrerarbeid, oppussing, renovering, terrasse, takbytte, kledning, kjøkkenmontering, dører og vinduer, graving og tilbygg i Halden og omegn. Be om pristilbud." path="/" image="https://nouroziebygganlegg.no/og-cover.jpg" />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F1210] text-white" data-testid="hero-section">
        <div className="absolute inset-0">
          {heroImg && <img src={heroImg} alt="NOUROZI byggeprosjekt i Halden – tømrer og snekker" className="w-full h-full object-cover transition-opacity duration-500" loading="eager" fetchpriority="high" decoding="async" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1210]/95 via-[#0F1210]/70 to-[#0F1210]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1210] via-transparent to-transparent" />
        </div>

        {/* Before/After toggle button top-right */}
        {hero.show_before_button !== false && heroBefore && heroAfter && (
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 flex flex-col gap-2 items-end">
            <button onClick={() => setShowBefore(v => !v)} className="inline-flex items-center gap-2 rounded-full bg-white/95 hover:bg-white text-[#0F1210] font-bold px-3.5 py-2 shadow-lg text-xs sm:text-sm backdrop-blur-sm" data-testid="hero-before-toggle">
              <RotateCw size={14}/> {showBefore ? t("hero.showAfter") : t("hero.showBefore")}
            </button>
            <button onClick={() => setOpenCompare(true)} className="inline-flex items-center gap-2 rounded-full bg-[#C49A6C]/95 hover:bg-[#C49A6C] text-[#0F1210] font-bold px-3.5 py-2 shadow-lg text-xs sm:text-sm" data-testid="hero-compare-open">
              <ImageIcon size={14}/> {t("hero.beforeAfter")}
            </button>
          </div>
        )}

        <div className="relative container-page pt-16 pb-24 sm:pt-24 sm:pb-32 lg:pt-32 lg:pb-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#C49A6C]/30 bg-[#C49A6C]/10 px-3.5 py-1.5 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C49A6C]" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#E8D6B6]" data-testid="hero-eyebrow">{t("hero.eyebrow")}</span>
            </div>
            <h1 className="mt-6 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {t("hero.title1")}<br />
              <span className="text-[#C49A6C]">{t("hero.title2")}</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-[#D8D2C1] max-w-2xl leading-relaxed">{t("hero.desc")}</p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a href={`tel:${COMPANY.phoneClean}`} className="group inline-flex items-center justify-center gap-2.5 bg-[#1B3626] hover:bg-[#244633] text-white font-bold px-7 py-4 rounded-md shadow-lg shadow-black/30 text-[15px]" data-testid="hero-call-btn">
                <Phone size={18} /> {t("hero.callBtn")} {COMPANY.phone}
              </a>
              <Link to="/be-om-pristilbud" className="group inline-flex items-center justify-center gap-2.5 bg-white hover:bg-[#F7F5F0] text-[#0F1210] font-bold px-7 py-4 rounded-md text-[15px]" data-testid="hero-quote-btn">
                {t("cta.quote")} <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
              {trust.map((tr) => (
                <div key={tr.title} className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-md bg-[#C49A6C]/15 text-[#E8D6B6] grid place-items-center shrink-0 border border-[#C49A6C]/25">{tr.icon}</span>
                  <div>
                    <div className="text-[13px] font-bold text-white leading-tight">{tr.title}</div>
                    <div className="text-[11px] text-[#B8B29F] mt-0.5 leading-snug">{tr.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Before/After compare modal */}
      {openCompare && heroBefore && heroAfter && (
        <div className="fixed inset-0 z-40 bg-black/85 grid place-items-center p-4" onClick={() => setOpenCompare(false)} data-testid="hero-compare-modal">
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between text-white mb-3">
              <div className="font-display font-bold text-lg">{t("hero.beforeAfter")}</div>
              <button onClick={() => setOpenCompare(false)} className="w-9 h-9 rounded-full bg-white text-[#0F1210] grid place-items-center font-bold" data-testid="hero-compare-close">✕</button>
            </div>
            <BeforeAfter beforeUrl={heroBefore} afterUrl={heroAfter} />
            <div className="text-center text-[#C9C0A8] text-xs mt-2">{t("hero.dragToCompare")}</div>
          </div>
        </div>
      )}

      {/* ABOUT */}
      <section className="section bg-[#EFECE6]" data-testid="about-section">
        <div className="container-page grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <img src={homeAboutImg} alt="Håndverker i arbeid" className="rounded-xl w-full h-[420px] object-cover" loading="lazy" onError={(e)=>{e.currentTarget.src='https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1400&q=85&auto=format&fit=crop'}} />
          </div>
          <div className="lg:col-span-6">
            <span className="eyebrow">{t("section.about.eyebrow")}</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("section.about.title")}</h2>
            <div className="dot-divider mt-4" />
            <p className="mt-5 text-[#383E45] leading-relaxed">{t("section.about.desc")}</p>
            <ul className="mt-6 space-y-3">
              {["about.point.1","about.point.2","about.point.3","about.point.4","about.point.5","about.point.6"].map((k) => (<li key={k} className="flex items-start gap-2.5"><CheckCircle2 size={18} className="text-[#1B3626] mt-0.5 shrink-0" /><span>{t(k)}</span></li>))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/om-oss" className="btn-secondary" data-testid="about-more-btn">{t("cta.moreAbout")}</Link>
              <Link to="/be-om-pristilbud" className="btn-primary" data-testid="about-quote-btn">{t("cta.quote")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section" data-testid="services-section">
        <div className="container-page">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="eyebrow">{t("section.services.eyebrow")}</span>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1C1F22]">{t("section.services.title")}</h2>
              <div className="dot-divider mt-4" />
            </div>
            <Link to="/tjenester" className="link-underline font-semibold text-[#1B3626]" data-testid="see-all-services">{t("section.services.seeAll")} →</Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 9).map((s) => (
              <Link key={s.slug} to={`/tjenester/${s.slug}`} className="card-warm block" data-testid={`svc-${s.slug}`}>
                <div className="aspect-[16/10] overflow-hidden"><img src={imgUrl(s)} alt={s.title} className="w-full h-full object-cover" loading="lazy" /></div>
                <div className="p-5">
                  <div className="flex items-center gap-2"><Hammer size={16} className="text-[#1B3626]" /><h3 className="font-display font-bold text-[17px]">{s.title}</h3></div>
                  <p className="text-sm text-[#555E68] mt-1.5">{s.short}</p>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1B3626]">Les mer <ArrowRight size={14} /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section bg-[#EFECE6]" data-testid="projects-section">
        <div className="container-page">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="eyebrow">{t("section.projects.eyebrow")}</span>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("section.projects.title")}</h2>
              <div className="dot-divider mt-4" />
            </div>
            <Link to="/prosjekter" className="link-underline font-semibold text-[#1B3626]" data-testid="see-all-projects">{t("section.projects.seeAll")} →</Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((p) => (
              <Link to={`/prosjekter/${p.id}`} key={p.id} className="card-warm block" data-testid={`proj-${p.id}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={projectCover(p) || projectFallback} alt={p.title} className="w-full h-full object-cover" loading="lazy" /></div>
                <div className="p-5">
                  <span className="text-xs font-bold text-[#1B3626] uppercase tracking-widest">{p.category}</span>
                  <h3 className="mt-1.5 font-display font-bold text-lg">{p.title}</h3>
                  <div className="text-sm text-[#555E68] mt-1">{p.location}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="section bg-[#181B18] text-white" data-testid="testimonials-section">
          <div className="container-page">
            <span className="eyebrow text-[#C49A6C]">{t("section.testimonials.eyebrow")}</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("section.testimonials.title")}</h2>
            <div className="dot-divider mt-4" />
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {testimonials.slice(0,3).map((tst) => (
                <div key={tst.id} className="bg-[#212522] border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-1 text-[#E8D6B6]">{"★".repeat(tst.rating || 5)}</div>
                  <p className="mt-3 text-[#E5E1D3] leading-relaxed">“{tst.text}”</p>
                  <div className="mt-4 text-sm"><div className="font-bold">– {tst.name}, {tst.location}</div></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE FORM */}
      <section className="section bg-[#F7F5F0]" data-testid="home-quote-section">
        <div className="container-page grid gap-10 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">{t("cta.quote")}</span>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("quote.title")}</h2>
            <div className="dot-divider mt-4" />
            <p className="mt-5 text-[#383E45] leading-relaxed">{t("quote.subtitle")}</p>
            <a href={`tel:${COMPANY.phoneClean}`} className="btn-primary mt-6" data-testid="cta-call-inline"><Phone size={16} /> {t("hero.callBtn")} {COMPANY.phone}</a>
            <div className="mt-8 space-y-3 text-sm text-[#555E68]">
              <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#1B3626]" /> {t("trust.reply")}</div>
              <div className="flex items-center gap-2"><Ruler size={16} className="text-[#1B3626]" /> {t("trust.free")}</div>
              <div className="flex items-center gap-2"><PaintbrushVertical size={16} className="text-[#1B3626]" /> {t("trust.smallBig")}</div>
            </div>
          </div>
          <div className="lg:col-span-7 w-full">
            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-6 sm:p-8"><QuoteForm compact /></div>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </div>
  );
}
